import React, { useRef, useMemo, useContext, useLayoutEffect } from "react";
import classNames from "classnames";
import styles from "./RadioButton.module.scss";
import { Tooltip, Tag, Text } from "./../../";
import uuidv4 from "uuid/v4";

export const RadioCircle = () => {
    return <svg overflow="visible" className={styles["circle"]}>
        <circle stroke="1" cx="8.5" cy="8.5" r="8" className={styles["outer-circle"]}/>
        <circle stroke="none" cx="8.5" cy="8.5" r="1" className={classNames(styles["inner-circle"])}/>
    </svg>
}

const RadioSelectionGroupContext = React.createContext(null);

export const RadioSelectionGroup = ({ form, name, children }) => {
    const idRef = useRef(uuidv4());
    const ref = useRef();

    useLayoutEffect(() => {
        const accordionTableHeaderStyle = window.document.createElement('style');
        accordionTableHeaderStyle.type = 'text/css';

        window.document.head.appendChild(accordionTableHeaderStyle);

        let max = 0;
        for (const card of ref.current.getElementsByClassName(`rsg-tag-${idRef.current}`)) {
            max = Math.max(card.clientWidth, max);
        }

        accordionTableHeaderStyle.innerHTML = `.rsg-tag-${idRef.current} { width: ${max}px;  }`;

        return () => {
            window.document.head.removeChild(accordionTableHeaderStyle);
        };
    }, [children, idRef]);

    const controller = useMemo(() => {
        return {
            form,
            name,
            id: idRef.current
        }
    }, [form, name, idRef]);

    return <RadioSelectionGroupContext.Provider value={controller}>
        <div className={styles['selection-group']} ref={ref}>
            {children}
        </div>
    </RadioSelectionGroupContext.Provider>
}

export const RadioSelection = ({ value, label, description, tagText, tagColor, onChange: _onChange, checked: _checked, isDisabled, className, tooltip, style, children, ...rest }) => {
    const { form, name, id } = useContext(RadioSelectionGroupContext);

    let onChange = _onChange;
    let checked = _checked;
    let showError = false;
    if (form && name) {
        checked = checked || form.formState[name] === value;
        onChange = onChange || form.handleFormChange;
        showError = form.submissionAttempted && form.formErrors[name]
    }

    return (
        <label className={classNames(styles["selection"], { [styles["disabled"]]: isDisabled, [styles["active"]]: checked, [styles["error"]]: showError }, className)} style={style}>
            <input checked={checked} onChange={onChange} type="radio" name={name} value={value} disabled={isDisabled} {...rest}/>
            <div className={styles['radio-wrapper']}>
                <RadioCircle/>
            </div>
            {tooltip && <Tooltip>{tooltip}</Tooltip>}
            {tagText && <Tag color={isDisabled ? "disabled" : tagColor} className={`${styles.tag} rsg-tag-${id}`}>{tagText}</Tag>}
            <div>
                <Text className={styles.label} bold level={13} color={isDisabled ? "text-disabled" : undefined}>{label}</Text>
                <Text color={isDisabled ? "text-disabled" : undefined}>{description}</Text>
            </div>
            {children && <div className={styles["selection-children"]}>
                {children}
            </div>}
        </label>
    );
}

export default ({ form, name, value, children, onChange: _onChange, checked: _checked, disabled, className, tooltip, style, ...rest }) => {
    let onChange = _onChange;
    let checked = _checked;
    let showError = false;
    if (form && name) {
        checked = checked || form.formState[name] === value;
        onChange = onChange || form.handleFormChange;
        showError = form.submissionAttempted && form.formErrors[name]
    }
    return (
        <label className={classNames(styles["default"], { [styles["disabled"]]: disabled, [styles["active"]]: checked, [styles["error"]]: showError }, className)} style={style}>
            <input checked={checked} onChange={onChange} type="radio" name={name} value={value} disabled={disabled} {...rest}/>
            <RadioCircle/>
            {tooltip && <Tooltip>{tooltip}</Tooltip>}
            {children}
        </label>
    );
};
